/**
 * Customer rating on the order detail page: shows the review, lets admins hide/show it,
 * or record the rating on the customer's behalf once the order is completed.
 */
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Rating,
  TextField,
  Typography,
} from '@mui/material';
import { StarRate as StarIcon, VisibilityOff as HiddenIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import moment from 'moment';
import { usePermissions } from '../../hooks/usePermissions';
import { useSubmitOrderReviewMutation, useUpdateReviewMutation } from '../../store/api/reviewsApi';
import type { Order } from '../../types/order';

const errorMessage = (err: unknown, fallback: string) => {
  const e = err as { data?: { message?: string; errors?: Record<string, unknown> } };
  const first = e?.data?.errors && Object.values(e.data.errors)[0];
  if (Array.isArray(first) && first.length) return String(first[0]);
  if (typeof first === 'string') return first;
  return e?.data?.message || fallback;
};

const OrderReviewPanel = ({ order }: { order: Order }) => {
  const { t } = useTranslation();
  const { hasPermission, isSuperUser } = usePermissions();
  const canView = isSuperUser() || hasPermission('view_reviews') || hasPermission('manage_reviews');
  const canManage = isSuperUser() || hasPermission('manage_reviews');

  const review = order.review;
  const hasProvider = !!order.assigned_provider;

  const [serviceRating, setServiceRating] = useState<number | null>(null);
  const [providerRating, setProviderRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [submitReview, { isLoading: submitting }] = useSubmitOrderReviewMutation();
  const [updateReview, { isLoading: updating }] = useUpdateReviewMutation();

  if (!canView) return null;
  // Nothing to show until the order is completed
  if (!review && !order.can_rate) return null;

  const handleSubmit = async () => {
    if (!serviceRating) {
      toast.error(t('reviews.form.serviceRatingRequired'));
      return;
    }
    try {
      await submitReview({
        order_id: order.id,
        service_rating: serviceRating,
        provider_rating: hasProvider ? providerRating : null,
        comment: comment.trim(),
      }).unwrap();
      toast.success(t('reviews.toast.saved'));
    } catch (err) {
      toast.error(errorMessage(err, t('reviews.toast.saveFailed')));
    }
  };

  const toggleHidden = async () => {
    if (!review) return;
    try {
      await updateReview({ id: review.id, is_hidden: !review.is_hidden }).unwrap();
      toast.success(review.is_hidden ? t('reviews.toast.shown') : t('reviews.toast.hidden'));
    } catch (err) {
      toast.error(errorMessage(err, t('reviews.toast.updateFailed')));
    }
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <StarIcon sx={{ mr: 1, color: 'warning.main' }} />
            <Typography variant="h6" fontWeight={600}>
              {t('reviews.panel.title')}
            </Typography>
          </Box>
          {review && canManage && (
            <Button size="small" variant="outlined" onClick={toggleHidden} disabled={updating}>
              {review.is_hidden ? t('reviews.actions.show') : t('reviews.actions.hide')}
            </Button>
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />

        {review ? (
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            {review.is_hidden && (
              <Alert severity="warning" icon={<HiddenIcon />}>
                {t('reviews.panel.hiddenNote')}
              </Alert>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ minWidth: 140 }}>
                {t('reviews.fields.serviceRating')}
              </Typography>
              <Rating value={review.service_rating} readOnly />
            </Box>
            {review.provider_rating != null && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ minWidth: 140 }}>
                  {t('reviews.fields.providerRating')}
                  {review.provider ? ` (${review.provider.name})` : ''}
                </Typography>
                <Rating value={review.provider_rating} readOnly />
              </Box>
            )}
            {review.comment && (
              <Typography variant="body1" sx={{ whiteSpace: 'pre-line', bgcolor: 'action.hover', p: 1.5, borderRadius: 1 }}>
                “{review.comment}”
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={t(`reviews.source.${review.source}`)}
                color={review.source === 'APP' ? 'primary' : 'default'}
                variant="outlined"
              />
              <Typography variant="caption" color="text.secondary">
                {moment(review.created_at).format('DD MMM YYYY HH:mm')}
                {review.created_by_name ? ` · ${t('reviews.panel.enteredBy', { name: review.created_by_name })}` : ''}
              </Typography>
            </Box>
            {review.admin_notes && (
              <Typography variant="caption" color="text.secondary">
                {t('reviews.fields.adminNotes')}: {review.admin_notes}
              </Typography>
            )}
          </Box>
        ) : canManage ? (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('reviews.panel.notRatedYet')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ minWidth: 140 }}>
                {t('reviews.fields.serviceRating')} *
              </Typography>
              <Rating value={serviceRating} onChange={(_e, v) => setServiceRating(v)} />
            </Box>
            {hasProvider && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ minWidth: 140 }}>
                  {t('reviews.fields.providerRating')} ({order.assigned_provider?.name})
                </Typography>
                <Rating value={providerRating} onChange={(_e, v) => setProviderRating(v)} />
              </Box>
            )}
            <TextField
              label={t('reviews.fields.comment')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              multiline
              minRows={2}
              fullWidth
            />
            <Box>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || !serviceRating}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
              >
                {t('reviews.actions.recordOnBehalf')}
              </Button>
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('reviews.panel.notRatedYet')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default OrderReviewPanel;
